import React, { useEffect, useState } from 'react';
import { Provider as StoreProvider } from 'react-redux';
import { store } from './store/store';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from './store/hooks';
import { setAuthState, clearAuthState } from './store/slices/authSlice';
import { supabase } from './api/supabase';
import MobileRedirectPage from './MobileRedirectPage';

// Admin screens
import AdminProtectedRoute from './components/AdminProtectedRoute';
import AdminDashboard from './screens/admin/AdminDashboard';
import AdminAnalyticsScreen from './screens/admin/AdminAnalyticsScreen';
import AdminVideosScreen from './screens/admin/AdminVideosScreen';
import AdminPhotosScreen from './screens/admin/AdminPhotosScreen';
import ReportsListScreen from './screens/admin/ReportsListScreen';
import ReportDetailScreen from './screens/admin/ReportDetailScreen';
import MonitoringDashboard from './screens/admin/MonitoringDashboard';
import VerificationReviewScreen from './screens/admin/VerificationReviewScreen';
import UserManagementScreen from './screens/admin/UserManagementScreen';

// Auth screen (admin login only)
import LoginScreen from './screens/auth/LoginScreen';

// Public content & legal screens
import ChildSafetyScreen from './screens/content/ChildSafetyScreen';
import DatingSafetyScreen from './screens/content/DatingSafetyScreen';
import BlogScreen from './screens/content/BlogScreen';
import BlogPostScreen from './screens/content/BlogPostScreen';
import FeaturesScreen from './screens/content/FeaturesScreen';
import WebPrivacyPolicyScreen from './screens/settings/WebPrivacyPolicyScreen';
import WebTermsOfServiceScreen from './screens/settings/WebTermsOfServiceScreen';
import WebAboutUsScreen from './screens/settings/WebAboutUsScreen';
import WebCookiePolicyScreen from './screens/settings/WebCookiePolicyScreen';
import WebFAQScreen from './screens/settings/WebFAQScreen';

import './App.css';
import './polyfills/url-polyfill';

const theme = createTheme({
  palette: {
    primary: {
      main: '#A76FFF',
    },
    secondary: {
      main: '#FF5A76',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
  },
});

// Paths that render the full router instead of MobileRedirectPage
const PUBLIC_APP_PATHS = [
  '/admin',
  '/login',
  '/child-safety',
  '/privacy-policy',
  '/terms-of-service',
  '/about-us',
  '/cookie-policy',
  '/faq',
  '/dating-safety',
  '/blog',
  '/features',
  '/auth/',
  '/reset-password',
  '/forgot-password',
  '/subscription/',
];
const useFullRouter = PUBLIC_APP_PATHS.some(path =>
  window.location.pathname.startsWith(path)
);

// Minimal auth wrapper for admin — sets up Supabase session listener
const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          dispatch(setAuthState({ user: session?.user || null, session }));
        } else if (event === 'SIGNED_OUT') {
          dispatch(clearAuthState());
        }
      }
    );

    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      dispatch(setAuthState({
        user: data.session?.user || null,
        session: data.session
      }));
      setIsLoading(false);
    };
    checkAuth();

    return () => { subscription.unsubscribe(); };
  }, [dispatch]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#FFFFFF' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return <>{children}</>;
};

// Admin login page — redirects to /admin after login
const AdminLoginRedirect: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);

  if (user) {
    return <Navigate to="/admin" replace />;
  }

  return <LoginScreen />;
};

// Router — admin routes + public content/legal pages
const AdminRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <AdminAuthProvider>
        <Routes>
          {/* Admin routes */}
          <Route path="/login" element={<AdminLoginRedirect />} />
          <Route path="/admin/login" element={<AdminLoginRedirect />} />
          <Route path="/admin/*" element={
            <AdminProtectedRoute>
              <Routes>
                <Route path="/" element={<AdminDashboard />} />
                <Route path="/reports" element={<ReportsListScreen />} />
                <Route path="/reports/pending" element={<ReportsListScreen />} />
                <Route path="/reports/resolved" element={<ReportsListScreen />} />
                <Route path="/reports/dismissed" element={<ReportsListScreen />} />
                <Route path="/reports/:reportId" element={<ReportDetailScreen />} />
                <Route path="/users" element={<UserManagementScreen />} />
                <Route path="/analytics" element={<AdminAnalyticsScreen />} />
                <Route path="/verification" element={<VerificationReviewScreen />} />
                <Route path="/videos" element={<AdminVideosScreen />} />
                <Route path="/photos" element={<AdminPhotosScreen />} />
                <Route path="/monitoring" element={<MonitoringDashboard />} />
                <Route path="*" element={<Navigate to="/admin" replace />} />
              </Routes>
            </AdminProtectedRoute>
          } />

          {/* Public content & legal pages */}
          <Route path="/child-safety" element={<ChildSafetyScreen />} />
          <Route path="/privacy-policy" element={<WebPrivacyPolicyScreen />} />
          <Route path="/terms-of-service" element={<WebTermsOfServiceScreen />} />
          <Route path="/about-us" element={<WebAboutUsScreen />} />
          <Route path="/cookie-policy" element={<WebCookiePolicyScreen />} />
          <Route path="/faq" element={<WebFAQScreen />} />
          <Route path="/dating-safety" element={<DatingSafetyScreen />} />
          <Route path="/blog" element={<BlogScreen />} />
          <Route path="/blog/:postId" element={<BlogPostScreen />} />
          <Route path="/features" element={<FeaturesScreen />} />

          {/* Everything else → mobile redirect */}
          <Route path="*" element={<MobileRedirectPage />} />
        </Routes>
      </AdminAuthProvider>
    </BrowserRouter>
  );
};

function App() {
  if (useFullRouter) {
    return (
      <StoreProvider store={store}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AdminRouter />
        </ThemeProvider>
      </StoreProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MobileRedirectPage />
    </ThemeProvider>
  );
}

export default App;
